import React, { useCallback, useEffect, useState } from "react";
import {
  Avatar, Box, Button, Chip, CircularProgress, IconButton, InputAdornment,
  List, ListItemButton, Menu, MenuItem, Paper, Skeleton, TextField,
  Tooltip, Typography, Badge,
} from "@mui/material";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import DraftsOutlinedIcon from "@mui/icons-material/DraftsOutlined";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import CreateOutlinedIcon from "@mui/icons-material/CreateOutlined";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import NoteAddOutlinedIcon from "@mui/icons-material/NoteAddOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ViewKanbanOutlinedIcon from "@mui/icons-material/ViewKanbanOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import BookOnlineOutlinedIcon from "@mui/icons-material/BookOnlineOutlined";
import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import StarOutlinedIcon from "@mui/icons-material/StarOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import PlaylistAddCheckOutlinedIcon from "@mui/icons-material/PlaylistAddCheckOutlined";
import {
  EmailStats, EmailMsg, EmailAccount, EmailThreadStatus,
  fetchStats, fetchAccounts, fetchStatuses,
  markThreadRead, triggerSync, archiveThread, updateThreadStatus,
  addThreadNote, deleteThreadNote, downloadAttachment,
} from "./emailHubApi";
import {
  EmailThreadV3, ThreadV3Filters, EmailHubActivity, CustomerInsights,
  EmailHubTask, TeamUser, AIAnalysisV3, LeadMatchState,
  fetchThreadsV3, fetchThreadDetailV3, fetchTeamUsers, aiAnalyzeV3,
  updateTeamState, updateThreadPriority, addNoteV3, fetchUnreadNotifCount,
  runAutomations, createTask, updateTask, deleteTask,
  reclassifyLeadMatch, ignoreLeadMatch,
  TEAM_STATES, PRIORITY_COLORS, NEXT_ACTIONS, PRIORITY_OPTIONS,
} from "./emailHubV3Api";
import ComposeModal from "./ComposeModal";
import CreateLeadModal from "./CreateLeadModal";
import CreateQuoteModal from "./CreateQuoteModal";
import ConvertBookingModal from "./ConvertBookingModal";
import AssignThreadModal from "./AssignThreadModal";
import ConvertToLeadModal from "./ConvertToLeadModal";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import LinkOffOutlinedIcon from "@mui/icons-material/LinkOffOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import AutoFixHighOutlinedIcon from "@mui/icons-material/AutoFixHighOutlined";
import { setEmailPrivacy, unlinkEmailFromLead, fetchLeadThread, convertEmailToLead, linkEmailToLead, STATUS_CONFIG, type LeadStatus, type LeadThread as LeadThreadType } from "../LeadSystem/leadApi";
import LeadThreadDrawer from "../LeadSystem/LeadThreadDrawer";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import { formatDateTime, formatShortDate, formatTooltipDate } from "../../utils/dateFormat";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import MeetingRoomOutlinedIcon from "@mui/icons-material/MeetingRoomOutlined";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import LocalFireDepartmentOutlinedIcon from "@mui/icons-material/LocalFireDepartmentOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import ReplyOutlinedIcon from "@mui/icons-material/ReplyOutlined";
import { useNavigate, useSearchParams } from "react-router-dom";

/* ── Lead detection keywords ───────────────────────────────────────────── */
const LEAD_KEYWORDS = /\b(booking|book|event|guests?|wedding|party|celebration|gala|reception|banquet|venue|hall|catering|availab|birthday|mehndi|engagement|anniversary|corporate|convocation|quote|quotation|estimate|pricing|price|package|inquiry|enquiry|host|hosting|get-together|gathering|function|conference|seminar|workshop|ceremony|dinner|luncheon|brunch|colleagues|organize|organise|capacity|services|requirements|rental)\b/i;

/* ── Theme ──────────────────────────────────────────────────────────────── */
const T = {
  navy: "#1E4D8F", gold: "#D4AF37", goldLight: "#f5eed9",
  white: "#fff", cream: "#faf8f4", bg: "#f4f2ee",
  slate: "#5c6b7e", slateLt: "#9ca8b7", line: "#eeebe6",
  green: "#2d6a35", greenBg: "#edf7ee",
  blue: "#1e40af", blueBg: "#eff6ff",
  red: "#9b2c2c", redBg: "#fef2f2",
  purple: "#6d28d9", purpleBg: "#f5f3ff",
  orange: "#c2410c", orangeBg: "#fff7ed",
};

const fmtDate = (d: string | null) => {
  if (!d) return "";
  const dt = new Date(d); const now = new Date();
  const diff = now.getTime() - dt.getTime();
  if (diff < 86400000 && dt.getDate() === now.getDate()) return dt.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  if (diff < 604800000) return dt.toLocaleDateString("en-US", { weekday: "short" });
  return dt.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};
const fmtFullDate = (d: string | null) => !d ? "" : new Date(d).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
const initials = (name: string | null, email: string) => (name && name.length >= 2) ? name.slice(0, 2).toUpperCase() : email.slice(0, 2).toUpperCase();

const INTENT_COLORS: Record<string, { bg: string; tx: string }> = {
  booking: { bg: T.greenBg, tx: T.green }, inquiry: { bg: T.blueBg, tx: T.blue },
  complaint: { bg: T.redBg, tx: T.red }, payment: { bg: T.goldLight, tx: "#8b6914" },
  follow_up: { bg: T.purpleBg, tx: T.purple }, cancellation: { bg: T.redBg, tx: T.red },
  general: { bg: T.bg, tx: T.slate },
};

const API_BASE = (window as any).REACT_APP_API_URL || "";

/* ═══════════════════════════════════════════════════════════════════════════ */
const EmailInbox: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [stats, setStats] = useState<EmailStats | null>(null);
  const [accounts, setAccounts] = useState<EmailAccount[]>([]);
  const [statuses, setStatuses] = useState<EmailThreadStatus[]>([]);
  const [threads, setThreads] = useState<EmailThreadV3[]>([]);
  const [totalThreads, setTotalThreads] = useState(0);
  const [selectedThread, setSelectedThread] = useState<EmailThreadV3 | null>(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [replyTo, setReplyTo] = useState<EmailMsg | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiData, setAiData] = useState<AIAnalysisV3 | null>(null);
  const [aiError, setAiError] = useState<string | null>(null);
  const [showAiPanel, setShowAiPanel] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [noteAdding, setNoteAdding] = useState(false);
  const [teamUsers, setTeamUsers] = useState<TeamUser[]>([]);
  const [notifCount, setNotifCount] = useState(0);

  // v3 modals
  const [leadModalOpen, setLeadModalOpen] = useState(false);
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  // Lead system modal + drawer
  const [convertLeadOpen, setConvertLeadOpen] = useState(false);
  const [leadDrawerOpen, setLeadDrawerOpen] = useState(false);
  const [leadDrawerThreadId, setLeadDrawerThreadId] = useState<number | null>(null);
  const [linkedLead, setLinkedLead] = useState<LeadThreadType | null>(null);
  // Auto-conversion state
  const [conversionStatus, setConversionStatus] = useState<"idle" | "converting">("idle");
  const [autoConverted, setAutoConverted] = useState(false);
  // Customer history — existing leads for same email (info-only, shown in right panel)
  const [customerHistory, setCustomerHistory] = useState<LeadThreadType[]>([]);
  const [linkingExisting, setLinkingExisting] = useState(false);
  const [linkConfirmLead, setLinkConfirmLead] = useState<LeadThreadType | null>(null);

  // v3 detail panels
  const [activities, setActivities] = useState<EmailHubActivity[]>([]);
  const [insights, setInsights] = useState<CustomerInsights | null>(null);
  const [threadTasks, setThreadTasks] = useState<EmailHubTask[]>([]);
  const [detailTab, setDetailTab] = useState<"messages" | "timeline" | "tasks">("messages");

  // Filters
  const [filters, setFilters] = useState<ThreadV3Filters>({ page: 1, per_page: 100 });
  const [search, setSearch] = useState("");
  const [sidebarTab, setSidebarTab] = useState<string>("all");

  // Status menu
  const [statusAnchor, setStatusAnchor] = useState<null | HTMLElement>(null);
  const [teamStateAnchor, setTeamStateAnchor] = useState<null | HTMLElement>(null);
  const [priorityAnchor, setPriorityAnchor] = useState<null | HTMLElement>(null);

  // Initialize from URL params
  useEffect(() => {
    const urlFilters: ThreadV3Filters = { page: 1, per_page: 100 };
    if (searchParams.get("unassigned")) urlFilters.unassigned = "true";
    if (searchParams.get("overdue")) urlFilters.overdue = "true";
    if (searchParams.get("follow_up")) urlFilters.follow_up = "true";
    if (searchParams.get("vip")) urlFilters.vip = "true";
    setFilters(urlFilters);
  }, []);

  const loadThreads = useCallback(async () => {
    setLoading(true);
    try {
      const d = await fetchThreadsV3(filters);
      setThreads(d.data);
      setTotalThreads(d.total);
    } catch {}
    setLoading(false);
  }, [filters]);

  const loadMeta = useCallback(async () => {
    const [s, a, st, u, nc] = await Promise.all([
      fetchStats().catch(() => null),
      fetchAccounts().catch(() => []),
      fetchStatuses().catch(() => []),
      fetchTeamUsers().catch(() => []),
      fetchUnreadNotifCount().catch(() => ({ count: 0 })),
    ]);
    if (s) setStats(s);
    setAccounts(a as EmailAccount[]);
    setStatuses(st as EmailThreadStatus[]);
    setTeamUsers(u as TeamUser[]);
    setNotifCount((nc as { count: number }).count);
  }, []);

  useEffect(() => { loadThreads(); }, [loadThreads]);
  useEffect(() => { loadMeta(); }, [loadMeta]);

  // Auto-check for new emails every 30 seconds (lightweight stats check)
  const lastUnreadRef = React.useRef<number | null>(null);
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const s = await fetchStats();
        const currentUnread = s.total_unread ?? 0;
        const totalThreads = s.accounts?.reduce((sum: number, a: any) => sum + (a.total || 0), 0) ?? 0;
        if (lastUnreadRef.current !== null && (currentUnread !== lastUnreadRef.current || totalThreads !== threads.length)) {
          loadThreads();
          setStats(s);
        }
        lastUnreadRef.current = currentUnread;
      } catch {}
    }, 30000);
    return () => clearInterval(interval);
  }, [loadThreads, threads.length]);

  useEffect(() => {
    const t = setTimeout(() => setFilters(p => ({ ...p, search: search || undefined, page: 1 })), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    const f: ThreadV3Filters = { page: 1, per_page: 100, search: filters.search };
    if (sidebarTab === "unread") f.unread = "true";
    else if (sidebarTab === "overdue") f.overdue = "true";
    else if (sidebarTab === "sent") f.direction = "outgoing";
    else if (sidebarTab === "unassigned") f.unassigned = "true";
    else if (sidebarTab === "follow_up") f.follow_up = "true";
    else if (sidebarTab === "vip") f.vip = "true";
    else if (sidebarTab === "linked") f.linked = "true";
    else if (sidebarTab === "not_linked") f.not_linked = "true";
    else if (sidebarTab === "private") f.is_private = "true";
    else if (sidebarTab === "my_threads") {
      // This would need current user ID — we'll use a simple approach
    }
    else if (sidebarTab.startsWith("account-")) f.account_id = Number(sidebarTab.replace("account-", ""));
    setFilters(f);
  }, [sidebarTab]);

  const handleSelectThread = async (thread: EmailThreadV3) => {
    setDetailLoading(true);
    setAiData(null); setAiError(null); setShowAiPanel(false);
    setDetailTab("messages"); setLinkedLead(null);
    // Reset conversion state on every thread switch
    setConversionStatus("idle"); setCustomerHistory([]); setAutoConverted(false); setLinkConfirmLead(null);

    try {
      const resp = await fetchThreadDetailV3(thread.id);
      setSelectedThread(resp.thread);
      setActivities(resp.activities || []);
      setInsights(resp.insights || null);
      setThreadTasks(resp.thread.tasks || []);

      // Fetch linked lead data if already linked
      if (resp.thread.linked_thread_id) {
        try {
          const lead = await fetchLeadThread(resp.thread.linked_thread_id);
          setLinkedLead(lead);
        } catch { setLinkedLead(null); }
      }
      // ── NO client-side auto-conversion ───────────────────────────────
      //
      // The previous build fired `convertEmailToLead()` here the moment
      // an unlinked thread was opened if a regex of lead keywords hit.
      // That created leads for vendor emails, "thanks" replies, and any
      // message that happened to mention "book" or "event". Killed.
      //
      // Conversion now requires the rep to click [Create Lead] in the
      // gated action row below — and that button only renders when the
      // backend's strict classifier marks the thread `full_match` or
      // `existing_match`. Partial / non-lead / needs-review threads
      // stay quietly in the inbox.

      await markThreadRead(thread.id);
      if (thread.unread_count > 0) {
        setThreads(prev => prev.map(t => t.id === thread.id ? { ...t, unread_count: 0 } : t));
        loadMeta();
      }
      try {
        const refreshed = await fetchThreadDetailV3(thread.id);
        setActivities(refreshed.activities || []);
      } catch {}
    } catch {}
    setDetailLoading(false);
  };

  const handleSync = async () => { setSyncing(true); try { await triggerSync(); loadThreads(); loadMeta(); } catch {} setSyncing(false); };
  const handleArchive = async (id: number) => { try { await archiveThread(id); setSelectedThread(null); loadThreads(); } catch {} };
  const handleAiAnalyze = async (threadId: number) => {
    setAiLoading(true); setShowAiPanel(true); setAiError(null); setAiData(null);
    try {
      const r = await aiAnalyzeV3(threadId);
      setAiData(r);
    } catch (e: any) {
      const msg = e?.message || e?.error?.message || "AI analysis failed. Please check your API key configuration.";
      setAiError(msg);
    }
    setAiLoading(false);
  };

  const handleStatusChange = async (statusId: number) => {
    if (!selectedThread) return;
    try {
      const updated = await updateThreadStatus(selectedThread.id, statusId);
      setSelectedThread(prev => prev ? { ...prev, status_id: statusId, status: updated.status } : null);
      setThreads(prev => prev.map(t => t.id === selectedThread.id ? { ...t, status_id: statusId } : t));
    } catch {}
    setStatusAnchor(null);
  };

  const handleTeamStateChange = async (state: string) => {
    if (!selectedThread) return;
    try {
      await updateTeamState(selectedThread.id, state);
      setSelectedThread(prev => prev ? { ...prev, team_state: state } : null);
      setThreads(prev => prev.map(t => t.id === selectedThread.id ? { ...t, team_state: state } : t));
    } catch {}
    setTeamStateAnchor(null);
  };

  const handlePriorityChange = async (priority: string) => {
    if (!selectedThread) return;
    try {
      await updateThreadPriority(selectedThread.id, priority);
      setSelectedThread(prev => prev ? { ...prev, priority: priority as any } : null);
      setThreads(prev => prev.map(t => t.id === selectedThread.id ? { ...t, priority: priority as any } : t));
    } catch {}
    setPriorityAnchor(null);
  };

  const handleAddNote = async () => {
    if (!selectedThread || !noteText.trim()) return;
    setNoteAdding(true);
    try {
      const note = await addNoteV3(selectedThread.id, noteText);
      setSelectedThread(prev => prev ? { ...prev, notes: [note, ...(prev.notes || [])] } : null);
      setNoteText("");
    } catch {}
    setNoteAdding(false);
  };

  const handleDeleteNote = async (noteId: number) => {
    try {
      await deleteThreadNote(noteId);
      setSelectedThread(prev => prev ? { ...prev, notes: (prev.notes || []).filter(n => n.id !== noteId) } : null);
    } catch {}
  };

  const handleDownloadAttachment = async (attId: number) => {
    try {
      const r = await downloadAttachment(attId);
      if (r.url) window.open(`${API_BASE}/../${r.url}`, "_blank");
    } catch {}
  };

  const handleRunAutomations = async () => {
    try { await runAutomations(); loadThreads(); } catch {}
  };

  const page = filters.page || 1;
  const lastPage = Math.ceil(totalThreads / (filters.per_page || 30));

  return (
    <Box sx={{ display: "flex", height: "calc(100vh - 70px)", bgcolor: T.bg, overflow: "hidden" }}>
      {/* ═══════════ LEFT SIDEBAR ═══════════ */}
      <Paper sx={{ width: 220, flexShrink: 0, borderRadius: 0, borderRight: `1px solid ${T.line}`, display: "flex", flexDirection: "column", bgcolor: T.white }}>
        <Box sx={{ p: 2 }}>
          <Button fullWidth variant="contained" startIcon={<CreateOutlinedIcon sx={{ fontSize: 16 }} />}
            onClick={() => { setReplyTo(null); setComposeOpen(true); }}
            sx={{ bgcolor: T.navy, color: T.white, textTransform: "none", fontWeight: 700, fontSize: 13, borderRadius: 2.5, py: 1.1, "&:hover": { bgcolor: T.gold, color: T.navy } }}>
            Compose
          </Button>
        </Box>
        <List sx={{ px: 1, flex: 1, overflow: "auto" }}>
          {[
            { key: "all", label: "All Mail", icon: <InboxOutlinedIcon sx={{ fontSize: 18 }} />, badge: stats?.total_unread },
            ...accounts.map(a => ({
              key: `account-${a.id}`, label: a.email.split("@")[0] + "@",
              icon: <MailOutlineIcon sx={{ fontSize: 18 }} />,
              badge: stats?.accounts.find(s => s.id === a.id)?.unread,
            })),
            { key: "sent", label: "Sent", icon: <SendOutlinedIcon sx={{ fontSize: 18 }} />, badge: 0 },
            { key: "unread", label: "Unread", icon: <DraftsOutlinedIcon sx={{ fontSize: 18 }} />, badge: stats?.total_unread },
            { key: "overdue", label: "Overdue", icon: <WarningAmberIcon sx={{ fontSize: 18 }} />, badge: stats?.total_overdue },
            { key: "unassigned", label: "Unassigned", icon: <PersonOutlineIcon sx={{ fontSize: 18 }} />, badge: 0 },
            { key: "follow_up", label: "Follow Up", icon: <FlagOutlinedIcon sx={{ fontSize: 18 }} />, badge: 0 },
            { key: "vip", label: "VIP", icon: <StarOutlinedIcon sx={{ fontSize: 18 }} />, badge: 0 },
            { key: "linked", label: "Linked", icon: <LinkOutlinedIcon sx={{ fontSize: 18 }} />, badge: 0 },
            { key: "not_linked", label: "Not Linked", icon: <LinkOffOutlinedIcon sx={{ fontSize: 18 }} />, badge: 0 },
            { key: "private", label: "Private", icon: <LockOutlinedIcon sx={{ fontSize: 18 }} />, badge: 0 },
          ].map(item => (
            <ListItemButton key={item.key} selected={sidebarTab === item.key} onClick={() => setSidebarTab(item.key)}
              sx={{
                borderRadius: 2, mb: 0.3, py: 0.8, px: 1.5,
                "&.Mui-selected": { bgcolor: T.goldLight, color: T.navy, "& .MuiTypography-root": { fontWeight: 700 } },
                "&:hover": { bgcolor: T.cream },
              }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, width: "100%" }}>
                <Box sx={{ color: sidebarTab === item.key ? T.gold : T.slateLt }}>{item.icon}</Box>
                <Typography sx={{ fontSize: 12, flex: 1, color: T.navy }}>{item.label}</Typography>
                {(item.badge ?? 0) > 0 && (
                  <Chip label={item.badge} size="small" sx={{ height: 18, minWidth: 18, fontSize: 10, fontWeight: 700, bgcolor: item.key === "overdue" ? T.red : T.navy, color: T.white }} />
                )}
              </Box>
            </ListItemButton>
          ))}
        </List>
        <Box sx={{ p: 1, borderTop: `1px solid ${T.line}`, display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Button fullWidth size="small" startIcon={<DashboardOutlinedIcon sx={{ fontSize: 14 }} />}
            onClick={() => navigate("/email-hub/dashboard")}
            sx={{ textTransform: "none", fontSize: 11, color: T.slate, justifyContent: "flex-start", px: 1.5, "&:hover": { bgcolor: T.cream } }}>
            Dashboard
          </Button>
          <Button fullWidth size="small" startIcon={<ViewKanbanOutlinedIcon sx={{ fontSize: 14 }} />}
            onClick={() => navigate("/email-hub/pipeline")}
            sx={{ textTransform: "none", fontSize: 11, color: T.slate, justifyContent: "flex-start", px: 1.5, "&:hover": { bgcolor: T.cream } }}>
            Pipeline View
          </Button>
          <Button fullWidth size="small"
            startIcon={<Badge badgeContent={notifCount} color="error" variant="dot"><NotificationsOutlinedIcon sx={{ fontSize: 14 }} /></Badge>}
            sx={{ textTransform: "none", fontSize: 11, color: T.slate, justifyContent: "flex-start", px: 1.5, "&:hover": { bgcolor: T.cream } }}>
            Notifications
          </Button>
          <Button fullWidth size="small" startIcon={syncing ? <CircularProgress size={12} /> : <RefreshIcon sx={{ fontSize: 14 }} />}
            onClick={handleSync} disabled={syncing}
            sx={{ textTransform: "none", fontSize: 11, color: T.slate, justifyContent: "flex-start", px: 1.5, "&:hover": { bgcolor: T.cream } }}>
            {syncing ? "Syncing..." : "Sync Now"}
          </Button>
          <Button fullWidth size="small" startIcon={<AutoAwesomeOutlinedIcon sx={{ fontSize: 14 }} />}
            onClick={handleRunAutomations}
            sx={{ textTransform: "none", fontSize: 11, color: T.purple, justifyContent: "flex-start", px: 1.5, "&:hover": { bgcolor: T.purpleBg } }}>
            Run Automations
          </Button>
        </Box>
      </Paper>

      {/* ═══════════ THREAD LIST ═══════════ */}
      <Box sx={{ width: 380, flexShrink: 0, borderRight: `1px solid ${T.line}`, display: "flex", flexDirection: "column", bgcolor: T.white }}>
        <Box sx={{ p: 1.5, borderBottom: `1px solid ${T.line}` }}>
          <TextField fullWidth size="small" placeholder="Search emails..." value={search} onChange={e => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: T.slateLt }} /></InputAdornment>, sx: { fontSize: 12, borderRadius: 2.5, bgcolor: T.bg, "& fieldset": { border: "none" } } }} />
        </Box>
        <Box sx={{ flex: 1, overflow: "auto" }}>
          {loading ? (
            <Box sx={{ p: 2 }}>{[1,2,3,4,5,6].map(i => <Box key={i} sx={{ display: "flex", gap: 1.5, mb: 1.5 }}><Skeleton variant="circular" width={36} height={36} /><Box sx={{ flex: 1 }}><Skeleton width="60%" height={16} /><Skeleton width="80%" height={14} sx={{ mt: 0.5 }} /></Box></Box>)}</Box>
          ) : threads.length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center" }}><InboxOutlinedIcon sx={{ fontSize: 48, color: T.slateLt, opacity: 0.3 }} /><Typography sx={{ fontSize: 13, color: T.slateLt, mt: 1 }}>No emails found</Typography></Box>
          ) : threads.map(thread => {
            const latest = thread.latest_email;
            const isSelected = selectedThread?.id === thread.id;
            const isUnread = thread.unread_count > 0;
            const statusColor = statuses.find(s => s.id === thread.status_id)?.color;
            const assignee = thread.assignee;
            return (
              <Box key={thread.id} onClick={() => handleSelectThread(thread)} sx={{
                px: 2, py: 1.5, cursor: "pointer", borderBottom: `1px solid ${T.line}`,
                bgcolor: isSelected ? T.goldLight : thread.is_private ? "#fef2f240" : isUnread ? "#fefcf8" : T.white,
                borderLeft: `3px solid ${isSelected ? T.gold : thread.linked_thread_id ? "#16a34a" : statusColor || "transparent"}`,
                transition: "all 0.12s", "&:hover": { bgcolor: isSelected ? T.goldLight : T.cream },
              }}>
                <Box sx={{ display: "flex", gap: 1.2 }}>
                  <Avatar sx={{ width: 34, height: 34, bgcolor: isUnread ? T.navy : T.slateLt, fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
                    {initials(latest?.from_name ?? null, latest?.from_email || "")}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Typography noWrap sx={{ fontSize: 12, fontWeight: isUnread ? 700 : 500, color: T.navy, flex: 1 }}>
                        {latest?.from_name || latest?.from_email || "Unknown"}
                      </Typography>
                      <Typography sx={{ fontSize: 10, color: T.slateLt, flexShrink: 0, ml: 1 }}>{fmtDate(thread.last_message_at)}</Typography>
                    </Box>
                    <Typography noWrap sx={{ fontSize: 12, fontWeight: isUnread ? 600 : 400, color: isUnread ? T.navy : T.slate, mt: 0.2 }}>
                      {thread.subject}
                    </Typography>
                    <Typography noWrap sx={{ fontSize: 11, color: T.slateLt, mt: 0.2 }}>{latest?.body_preview || ""}</Typography>
                    <Box sx={{ display: "flex", gap: 0.5, mt: 0.5, alignItems: "center", flexWrap: "wrap" }}>
                      {isUnread && <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: T.blue }} />}
                      {thread.is_overdue && <Chip label="SLA" size="small" sx={{ height: 16, fontSize: 8, fontWeight: 700, bgcolor: T.redBg, color: T.red }} />}
                      {thread.is_vip && <Chip label="VIP" size="small" sx={{ height: 16, fontSize: 8, fontWeight: 700, bgcolor: "#fef3c7", color: "#92400e" }} />}
                      {thread.follow_up_required && <Chip label="Follow Up" size="small" sx={{ height: 16, fontSize: 8, fontWeight: 700, bgcolor: T.orangeBg, color: T.orange }} />}
                      {thread.priority && thread.priority !== "medium" && (
                        <Chip label={thread.priority} size="small" sx={{ height: 16, fontSize: 8, fontWeight: 700, bgcolor: PRIORITY_COLORS[thread.priority] + "20", color: PRIORITY_COLORS[thread.priority] }} />
                      )}
                      {latest?.has_attachments && <AttachFileIcon sx={{ fontSize: 12, color: T.slateLt, transform: "rotate(45deg)" }} />}
                      {thread.message_count > 1 && <Chip label={thread.message_count} size="small" sx={{ height: 16, fontSize: 9, fontWeight: 600, bgcolor: T.bg, color: T.slate }} />}
                      {thread.ai_intent && <Chip label={thread.ai_intent} size="small" sx={{ height: 16, fontSize: 9, fontWeight: 600, bgcolor: INTENT_COLORS[thread.ai_intent]?.bg || T.bg, color: INTENT_COLORS[thread.ai_intent]?.tx || T.slate }} />}
                      {/* Lead system badges */}
                      {thread.linked_thread_id ? (
                        <Chip icon={<LinkOutlinedIcon sx={{ fontSize: "10px !important" }} />} label="Linked" size="small" sx={{ height: 16, fontSize: 8, fontWeight: 700, bgcolor: "#dcfce7", color: "#16a34a" }} />
                      ) : (
                        <Chip label="Not Linked" size="small" sx={{ height: 16, fontSize: 8, fontWeight: 600, bgcolor: "#f1f5f9", color: "#94a3b8" }} />
                      )}
                      {thread.is_private && <Chip icon={<LockOutlinedIcon sx={{ fontSize: "10px !important" }} />} label="Private" size="small" sx={{ height: 16, fontSize: 8, fontWeight: 700, bgcolor: T.redBg, color: T.red }} />}
                      {thread.lead_id && <Chip label="Lead" size="small" sx={{ height: 16, fontSize: 8, fontWeight: 700, bgcolor: "#dbeafe", color: "#1e40af" }} />}
                      {thread.quote_id && <Chip label="Quote" size="small" sx={{ height: 16, fontSize: 8, fontWeight: 700, bgcolor: T.greenBg, color: T.green }} />}
                      {thread.booking_id && <Chip label="Booking" size="small" sx={{ height: 16, fontSize: 8, fontWeight: 700, bgcolor: T.orangeBg, color: T.orange }} />}
                      {/* Next action badge */}
                      {thread.next_action && thread.next_action !== "no_action" && NEXT_ACTIONS[thread.next_action] && (
                        <Chip label={NEXT_ACTIONS[thread.next_action].label} size="small"
                          sx={{ height: 16, fontSize: 8, fontWeight: 700, bgcolor: NEXT_ACTIONS[thread.next_action].color + "15", color: NEXT_ACTIONS[thread.next_action].color }} />
                      )}
                      {/* Assignee avatar */}
                      {assignee && (
                        <Tooltip title={(assignee as any).display_name || assignee.username} arrow>
                          <Avatar sx={{ width: 18, height: 18, fontSize: 8, bgcolor: "#6366f1" }}>
                            {((assignee as any).display_name || assignee.username)?.[0]?.toUpperCase() || "?"}
                          </Avatar>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
        {totalThreads > 0 && (
          <Box sx={{ p: 1, borderTop: `1px solid ${T.line}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography sx={{ fontSize: 10, color: T.slateLt }}>{totalThreads} conversations</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <IconButton size="small" disabled={page <= 1} onClick={() => setFilters(p => ({ ...p, page: page - 1 }))}><ChevronLeftIcon sx={{ fontSize: 16 }} /></IconButton>
              <Typography sx={{ fontSize: 10, color: T.slate }}>{page}/{lastPage || 1}</Typography>
              <IconButton size="small" disabled={page >= lastPage} onClick={() => setFilters(p => ({ ...p, page: page + 1 }))}><ChevronRightIcon sx={{ fontSize: 16 }} /></IconButton>
            </Box>
          </Box>
        )}
      </Box>

      {/* ═══════════ THREAD DETAIL PANEL — CRM WORKSPACE ═══════════ */}
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", bgcolor: T.white, overflow: "hidden" }}>
        {!selectedThread ? (
          /* ── Empty state ── */
          <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Box sx={{ textAlign: "center", maxWidth: 380, px: 3 }}>
              <Box sx={{ width: 72, height: 72, borderRadius: "50%", bgcolor: T.goldLight, display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2.5 }}>
                <MailOutlineIcon sx={{ fontSize: 36, color: T.gold }} />
              </Box>
              <Typography sx={{ fontSize: 17, fontWeight: 700, color: T.navy, mb: 0.5 }}>Select a conversation</Typography>
              <Typography sx={{ fontSize: 13, color: T.slateLt, mb: 3, lineHeight: 1.6 }}>
                View email details, link to leads, manage privacy, and open the full CRM — all from one workspace.
              </Typography>
              <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
                {[
                  { icon: <LinkOutlinedIcon sx={{ fontSize: 16 }} />, label: "Link to Lead", color: "#2563eb" },
                  { icon: <ViewKanbanOutlinedIcon sx={{ fontSize: 16 }} />, label: "Pipeline", color: T.purple },
                  { icon: <LockOutlinedIcon sx={{ fontSize: 16 }} />, label: "Privacy", color: T.red },
                  { icon: <AutoAwesomeOutlinedIcon sx={{ fontSize: 16 }} />, label: "AI Detect", color: T.green },
                ].map(f => (
                  <Box key={f.label} sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 1.5, py: 0.6, borderRadius: 2, bgcolor: T.bg }}>
                    <Box sx={{ color: f.color }}>{f.icon}</Box>
                    <Typography sx={{ fontSize: 10, fontWeight: 600, color: T.slate }}>{f.label}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        ) : detailLoading ? (
          <Box sx={{ p: 4 }}><Skeleton width="40%" height={24} /><Skeleton width="60%" height={16} sx={{ mt: 1 }} /><Skeleton width="100%" height={200} sx={{ mt: 2, borderRadius: 2 }} /></Box>
        ) : (
          <>
            {/* ═══════════ CRM HEADER SECTION ═══════════ */}
            {selectedThread.linked_thread_id && linkedLead ? (
              /* ── LINKED STATE: Lead info card ── */
              <Box sx={{ bgcolor: "#f0fdf4", borderBottom: "2px solid #bbf7d0" }}>
                <Box sx={{ px: 3, py: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Avatar sx={{ width: 38, height: 38, bgcolor: "#16a34a", fontWeight: 700, fontSize: 14 }}>
                      {linkedLead.name?.charAt(0)?.toUpperCase()}
                    </Avatar>
                    <Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography sx={{ fontSize: 15, fontWeight: 700, color: T.navy }}>{linkedLead.name}</Typography>
                        <Chip label={STATUS_CONFIG[linkedLead.status as LeadStatus]?.label || linkedLead.status} size="small"
                          sx={{ height: 20, fontSize: 9, fontWeight: 700, bgcolor: STATUS_CONFIG[linkedLead.status as LeadStatus]?.color || "#6b7280", color: "#fff" }} />
                        {linkedLead.ai_score && (
                          <Chip label={linkedLead.ai_score === "hot" ? "Hot" : linkedLead.ai_score === "warm" ? "Warm" : "Cold"} size="small"
                            icon={<LocalFireDepartmentOutlinedIcon sx={{ fontSize: "12px !important", color: "inherit !important" }} />}
                            sx={{ height: 20, fontSize: 9, fontWeight: 600,
                              bgcolor: linkedLead.ai_score === "hot" ? "#fef2f2" : linkedLead.ai_score === "warm" ? "#fffbeb" : "#eff6ff",
                              color: linkedLead.ai_score === "hot" ? "#dc2626" : linkedLead.ai_score === "warm" ? "#d97706" : "#2563eb" }} />
                        )}
                        {linkedLead.is_vip && <Chip label="VIP" size="small" sx={{ height: 20, fontSize: 9, fontWeight: 700, bgcolor: "#fef3c7", color: "#92400e" }} />}
                      </Box>
                      <Box sx={{ display: "flex", gap: 2, mt: 0.5 }}>
                        {linkedLead.event_type && (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                            <EventOutlinedIcon sx={{ fontSize: 12, color: T.slateLt }} />
                            <Typography sx={{ fontSize: 11, color: T.slate, textTransform: "capitalize" }}>{linkedLead.event_type.replace(/_/g, " ")}</Typography>
                          </Box>
                        )}
                        {linkedLead.event_date && (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                            <EventOutlinedIcon sx={{ fontSize: 12, color: T.slateLt }} />
                            <Typography sx={{ fontSize: 11, color: T.slate }}>{formatShortDate(linkedLead.event_date)}</Typography>
                          </Box>
                        )}
                        {linkedLead.guest_count && (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                            <PeopleOutlineIcon sx={{ fontSize: 12, color: T.slateLt }} />
                            <Typography sx={{ fontSize: 11, color: T.slate }}>{linkedLead.guest_count} guests</Typography>
                          </Box>
                        )}
                        {linkedLead.budget_value && (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                            <AttachMoneyOutlinedIcon sx={{ fontSize: 12, color: T.slateLt }} />
                            <Typography sx={{ fontSize: 11, color: T.slate }}>${Number(linkedLead.budget_value).toLocaleString()}</Typography>
                          </Box>
                        )}
                        {linkedLead.preferred_hall && (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                            <MeetingRoomOutlinedIcon sx={{ fontSize: 12, color: T.slateLt }} />
                            <Typography sx={{ fontSize: 11, color: T.slate }}>{linkedLead.preferred_hall}</Typography>
                          </Box>
                        )}
                        {linkedLead.assignee && (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                            <PersonOutlineIcon sx={{ fontSize: 12, color: T.slateLt }} />
                            <Typography sx={{ fontSize: 11, color: T.slate }}>{(linkedLead.assignee as any).display_name || linkedLead.assignee.username}</Typography>
                          </Box>
                        )}
                        <Typography sx={{ fontSize: 10, color: T.slateLt }}>Source: {linkedLead.source}</Typography>
                      </Box>
                    </Box>
                  </Box>
                  {/* Lead actions */}
                  <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0, alignItems: "center" }}>
                    {autoConverted && (
                      <Chip
                        label="✔ New lead created"
                        size="small"
                        sx={{ height: 20, fontSize: 9, fontWeight: 700, bgcolor: "#dcfce7", color: "#15803d", border: "1px solid #bbf7d0" }}
                      />
                    )}
                    <Button size="small" variant="contained" startIcon={<OpenInNewOutlinedIcon sx={{ fontSize: 14 }} />}
                      onClick={() => { setLeadDrawerThreadId(selectedThread.linked_thread_id); setLeadDrawerOpen(true); }}
                      sx={{ textTransform: "none", fontSize: 11, fontWeight: 700, bgcolor: "#16a34a", borderRadius: 1.5, "&:hover": { bgcolor: "#15803d" } }}>
                      View Lead
                    </Button>
                    <Button size="small" variant="outlined" startIcon={<LinkOffOutlinedIcon sx={{ fontSize: 14 }} />}
                      onClick={async () => {
                        try {
                          await unlinkEmailFromLead(selectedThread.id);
                          setSelectedThread(prev => prev ? { ...prev, linked_thread_id: null } : null);
                          setThreads(prev => prev.map(t => t.id === selectedThread.id ? { ...t, linked_thread_id: null } : t));
                          setLinkedLead(null);
                        } catch {}
                      }}
                      sx={{ textTransform: "none", fontSize: 11, color: T.slate, borderColor: T.line, borderRadius: 1.5, "&:hover": { borderColor: T.red, color: T.red } }}>
                      Unlink
                    </Button>
                    {!selectedThread.quote_id && (
                      <Button size="small" variant="outlined" startIcon={<RequestQuoteOutlinedIcon sx={{ fontSize: 14 }} />}
                        onClick={() => setQuoteModalOpen(true)}
                        sx={{ textTransform: "none", fontSize: 11, color: T.green, borderColor: "#bbf7d0", borderRadius: 1.5, "&:hover": { bgcolor: T.greenBg } }}>
                        Quote
                      </Button>
                    )}
                    <Tooltip title={selectedThread.is_private ? "Make Public" : "Mark Private"}>
                      <IconButton size="small" onClick={async () => {
                        const priv = !selectedThread.is_private;
                        try { await setEmailPrivacy(selectedThread.id, priv); setSelectedThread(prev => prev ? { ...prev, is_private: priv } : null); setThreads(prev => prev.map(t => t.id === selectedThread.id ? { ...t, is_private: priv } : t)); } catch {}
                      }}
                        sx={{ bgcolor: selectedThread.is_private ? T.redBg : T.bg, borderRadius: 1.5 }}>
                        {selectedThread.is_private ? <LockOutlinedIcon sx={{ fontSize: 16, color: T.red }} /> : <LockOpenOutlinedIcon sx={{ fontSize: 16, color: T.slate }} />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>
            ) : selectedThread.is_private ? (
              /* ── PRIVATE STATE ── */
              <Box sx={{ bgcolor: "#fef2f2", borderBottom: "2px solid #fecaca", px: 3, py: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <LockOutlinedIcon sx={{ fontSize: 20, color: T.red }} />
                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: T.red }}>Private Email</Typography>
                    <Typography sx={{ fontSize: 11, color: "#991b1b" }}>Hidden from Pipeline, Calendar & Inquiries</Typography>
                  </Box>
                </Box>
                <Button size="small" variant="outlined" startIcon={<LockOpenOutlinedIcon sx={{ fontSize: 14 }} />}
                  onClick={async () => {
                    try { await setEmailPrivacy(selectedThread.id, false); setSelectedThread(prev => prev ? { ...prev, is_private: false } : null); setThreads(prev => prev.map(t => t.id === selectedThread.id ? { ...t, is_private: false } : t)); } catch {}
                  }}
                  sx={{ textTransform: "none", fontSize: 11, color: T.red, borderColor: "#fecaca", borderRadius: 1.5, "&:hover": { bgcolor: "#fee2e2" } }}>
                  Remove Private
                </Button>
              </Box>
            ) : conversionStatus === "converting" ? (
              /* ── AUTO-CONVERTING ── */
              <Box sx={{ bgcolor: "#f0fdf4", borderBottom: "2px solid #bbf7d0", px: 3, py: 1.5, display: "flex", alignItems: "center", gap: 1.5 }}>
                <CircularProgress size={18} sx={{ color: "#16a34a", flexShrink: 0 }} />
                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#15803d" }}>Detecting lead opportunity…</Typography>
                  <Typography sx={{ fontSize: 11, color: "#22c55e" }}>AI identified a potential inquiry — creating lead automatically</Typography>
                </Box>
              </Box>

            ) : (
              /* ── NOT LINKED — strict AI lead-match gating ────────────
                 The banner palette + the action set depend entirely on
                 the backend classifier's `lead_match_state`. Partial /
                 non-lead / needs-review threads still let staff manually
                 convert (override) but do NOT shout about it. */
              <LeadMatchBanner
                state={selectedThread.lead_match_state ?? null}
                confidence={selectedThread.lead_match_confidence ?? null}
                reasons={selectedThread.lead_match_reasons ?? []}
                ignored={!!selectedThread.lead_match_ignored_at}
                linkedThread={selectedThread.linked_thread ?? null}
                onOpenLead={(leadId) => navigate(`/leads/${leadId}`)}
                onViewTimeline={(leadId) => navigate(`/leads/${leadId}#full-timeline-anchor`)}
                onCreateLead={() => setConvertLeadOpen(true)}
                onLinkExisting={() => setConvertLeadOpen(true)}
                onIgnore={async () => {
                  try {
                    await ignoreLeadMatch(selectedThread.id);
                    setSelectedThread(prev => prev ? {
                      ...prev,
                      lead_match_state: "non_lead",
                      lead_match_ignored_at: new Date().toISOString(),
                    } : null);
                    setThreads(prev => prev.map(t => t.id === selectedThread.id
                      ? { ...t, lead_match_state: "non_lead", lead_match_ignored_at: new Date().toISOString() }
                      : t));
                  } catch {}
                }}
                onReclassify={async () => {
                  try {
                    const r = await reclassifyLeadMatch(selectedThread.id);
                    setSelectedThread(prev => prev ? {
                      ...prev,
                      lead_match_state: r.lead_match_state,
                      lead_match_confidence: r.lead_match_confidence,
                      lead_match_reasons: r.lead_match_reasons,
                      // Reclassify can flip a thread to full_match AND
                      // auto-create the lead in the same request. Pick up
                      // the new linked_thread_id so the banner switches
                      // straight to the green "Lead Created" view.
                      linked_thread_id: r.linked_thread_id ?? prev.linked_thread_id,
                    } : null);
                    if (r.linked_thread_id) {
                      setThreads(prev => prev.map(t => t.id === selectedThread.id
                        ? { ...t, linked_thread_id: r.linked_thread_id ?? t.linked_thread_id, lead_match_state: r.lead_match_state }
                        : t));
                      try {
                        const lead = await fetchLeadThread(r.linked_thread_id);
                        setLinkedLead(lead);
                      } catch { /* banner still renders from linked_thread_id alone */ }
                    } else {
                      setThreads(prev => prev.map(t => t.id === selectedThread.id
                        ? { ...t, lead_match_state: r.lead_match_state }
                        : t));
                    }
                  } catch {}
                }}
                onMarkPrivate={async () => {
                  try {
                    await setEmailPrivacy(selectedThread.id, true);
                    setSelectedThread(prev => prev ? { ...prev, is_private: true } : null);
                    setThreads(prev => prev.map(t => t.id === selectedThread.id ? { ...t, is_private: true } : t));
                  } catch {}
                }}
              />
            )}

            {/* ═══════════ EMAIL HEADER ═══════════ */}
            <Box sx={{ px: 3, py: 1.5, borderBottom: `1px solid ${T.line}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 16, fontWeight: 700, color: T.navy, lineHeight: 1.3 }}>{selectedThread.subject}</Typography>
                <Box sx={{ display: "flex", gap: 0.6, mt: 0.6, flexWrap: "wrap", alignItems: "center" }}>
                  <Chip label={`${selectedThread.message_count} msg`} size="small" sx={{ height: 18, fontSize: 9, fontWeight: 600, bgcolor: T.bg, color: T.slate }} />
                  {selectedThread.account?.email && <Chip label={selectedThread.account.email} size="small" sx={{ height: 18, fontSize: 9, fontWeight: 600, bgcolor: T.blueBg, color: T.blue }} />}
                  {selectedThread.status && <Chip label={selectedThread.status.name} size="small" onClick={(e) => setStatusAnchor(e.currentTarget)} sx={{ height: 18, fontSize: 9, fontWeight: 600, bgcolor: selectedThread.status.color + "20", color: selectedThread.status.color, cursor: "pointer" }} />}
                  <Chip label={selectedThread.priority || "medium"} size="small" onClick={(e) => setPriorityAnchor(e.currentTarget)}
                    sx={{ height: 18, fontSize: 9, fontWeight: 600, cursor: "pointer", textTransform: "capitalize", bgcolor: PRIORITY_COLORS[selectedThread.priority || "medium"] + "20", color: PRIORITY_COLORS[selectedThread.priority || "medium"] }} />
                  {selectedThread.assignee ? (
                    <Chip avatar={<Avatar sx={{ width: 14, height: 14, fontSize: 7, bgcolor: "#6366f1" }}>{((selectedThread.assignee as any).display_name || selectedThread.assignee.username)?.[0]?.toUpperCase()}</Avatar>}
                      label={(selectedThread.assignee as any).display_name || selectedThread.assignee.username} size="small" onClick={() => setAssignModalOpen(true)}
                      sx={{ height: 18, fontSize: 9, fontWeight: 600, cursor: "pointer", bgcolor: "#eef2ff", color: "#6366f1" }} />
                  ) : (
                    <Chip icon={<PersonOutlineIcon sx={{ fontSize: 10 }} />} label="Assign" size="small" variant="outlined" onClick={() => setAssignModalOpen(true)}
                      sx={{ height: 18, fontSize: 9, cursor: "pointer", borderStyle: "dashed" }} />
                  )}
                  {selectedThread.is_overdue && <Chip icon={<WarningAmberIcon sx={{ fontSize: 10 }} />} label="SLA" size="small" sx={{ height: 18, fontSize: 9, fontWeight: 700, bgcolor: T.redBg, color: T.red }} />}
                  {selectedThread.is_vip && <Chip label="VIP" size="small" sx={{ height: 18, fontSize: 9, fontWeight: 700, bgcolor: "#fef3c7", color: "#92400e" }} />}
                </Box>
              </Box>
              {/* Action icons */}
              <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0, ml: 1.5 }}>
                <Tooltip title="Reply"><IconButton size="small" onClick={() => { setReplyTo(selectedThread.emails?.slice(-1)[0] || null); setComposeOpen(true); }}
                  sx={{ bgcolor: T.blueBg, borderRadius: 1.5, "&:hover": { bgcolor: "#dbeafe" } }}>
                  <SendOutlinedIcon sx={{ fontSize: 16, color: T.blue }} />
                </IconButton></Tooltip>
                <Tooltip title="AI Analyze"><IconButton size="small" onClick={() => handleAiAnalyze(selectedThread.id)} disabled={aiLoading}
                  sx={{ bgcolor: T.purpleBg, borderRadius: 1.5, "&:hover": { bgcolor: "#e9e5ff" } }}>
                  {aiLoading ? <CircularProgress size={14} /> : <AutoAwesomeOutlinedIcon sx={{ fontSize: 16, color: T.purple }} />}
                </IconButton></Tooltip>
                <Tooltip title="Assign"><IconButton size="small" onClick={() => setAssignModalOpen(true)}
                  sx={{ bgcolor: "#eef2ff", borderRadius: 1.5, "&:hover": { bgcolor: "#e0e7ff" } }}>
                  <AssignmentIndOutlinedIcon sx={{ fontSize: 16, color: "#6366f1" }} />
                </IconButton></Tooltip>
                <Tooltip title="Archive"><IconButton size="small" onClick={() => handleArchive(selectedThread.id)}
                  sx={{ bgcolor: T.bg, borderRadius: 1.5, "&:hover": { bgcolor: T.redBg } }}>
                  <ArchiveOutlinedIcon sx={{ fontSize: 16, color: T.slate }} />
                </IconButton></Tooltip>
              </Box>
            </Box>

            {/* Menus */}
            <Menu anchorEl={statusAnchor} open={Boolean(statusAnchor)} onClose={() => setStatusAnchor(null)}>
              {statuses.map(s => (
                <MenuItem key={s.id} onClick={() => handleStatusChange(s.id)} sx={{ fontSize: 12 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: s.color, mr: 1 }} /> {s.name}
                </MenuItem>
              ))}
            </Menu>
            <Menu anchorEl={priorityAnchor} open={Boolean(priorityAnchor)} onClose={() => setPriorityAnchor(null)}>
              {PRIORITY_OPTIONS.map(p => (
                <MenuItem key={p} onClick={() => handlePriorityChange(p)} sx={{ fontSize: 12 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: PRIORITY_COLORS[p], mr: 1 }} />
                  <Typography sx={{ textTransform: "capitalize" }}>{p}</Typography>
                </MenuItem>
              ))}
            </Menu>

            {/* ═══════════ TABS ═══════════ */}
            <Box sx={{ display: "flex", borderBottom: `1px solid ${T.line}`, px: 3 }}>
              {([
                { key: "messages", label: "Messages", icon: <MailOutlineIcon sx={{ fontSize: 14 }} /> },
                { key: "timeline", label: "Timeline", icon: <TimelineOutlinedIcon sx={{ fontSize: 14 }} /> },
                { key: "tasks", label: `Tasks (${threadTasks.length})`, icon: <TaskAltOutlinedIcon sx={{ fontSize: 14 }} /> },
              ] as const).map(tab => (
                <Button key={tab.key} startIcon={tab.icon} size="small"
                  onClick={() => setDetailTab(tab.key)}
                  sx={{
                    textTransform: "none", fontSize: 11, borderRadius: 0, py: 1, px: 2,
                    color: detailTab === tab.key ? T.navy : T.slateLt, fontWeight: detailTab === tab.key ? 700 : 500,
                    borderBottom: detailTab === tab.key ? `2px solid ${T.gold}` : "2px solid transparent",
                  }}>
                  {tab.label}
                </Button>
              ))}
            </Box>

            {/* ═══════════ TAB CONTENT + RIGHT SIDEBAR ═══════════ */}
            <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
              <Box sx={{ flex: 1, overflow: "auto", px: 3, py: 2 }}>
                {detailTab === "messages" && (
                  <>
                    {/* AI summary */}
                    {selectedThread.ai_summary && (
                      <Box sx={{ mb: 2, p: 1.2, borderRadius: 2, bgcolor: T.purpleBg, border: "1px solid #e9e5ff" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.3 }}>
                          <AutoAwesomeOutlinedIcon sx={{ fontSize: 12, color: T.purple }} />
                          <Typography sx={{ fontSize: 10, fontWeight: 700, color: T.purple }}>AI Summary</Typography>
                        </Box>
                        <Typography sx={{ fontSize: 11, color: T.purple, lineHeight: 1.5 }}>{selectedThread.ai_summary}</Typography>
                      </Box>
                    )}

                    {/* ── Viewed by strip ── */}
                    {(() => {
                      const viewActs = activities.filter(a => a.type === "thread_viewed");
                      const replyActs = activities.filter(a => a.type === "email_sent");
                      if (viewActs.length === 0 && replyActs.length === 0) return null;
                      const viewers = new Map<string, string>();
                      viewActs.forEach(a => {
                        const name = (a.user as any)?.display_name || a.user?.username || a.meta?.viewed_by || "Unknown";
                        if (!viewers.has(name)) viewers.set(name, a.created_at);
                      });
                      return (
                        <Box sx={{ mb: 2, p: 1.2, borderRadius: 2, bgcolor: "#f8fafc", border: `1px solid ${T.line}` }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                              <VisibilityOutlinedIcon sx={{ fontSize: 12, color: "#64748b" }} />
                              <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>Viewed by:</Typography>
                            </Box>
                            {Array.from(viewers.entries()).map(([name, time]) => (
                              <Tooltip key={name} title={`Viewed ${formatTooltipDate(time)}`}>
                                <Chip label={name} size="small" sx={{ height: 18, fontSize: 9, fontWeight: 600, bgcolor: "#e0e7ff", color: "#4338ca" }} />
                              </Tooltip>
                            ))}
                            {replyActs.length > 0 && (
                              <>
                                <Box sx={{ width: 1, height: 14, bgcolor: T.line, mx: 0.5 }} />
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                                  <ReplyOutlinedIcon sx={{ fontSize: 12, color: T.green }} />
                                  <Typography sx={{ fontSize: 10, fontWeight: 700, color: T.green, textTransform: "uppercase" }}>Replied by:</Typography>
                                </Box>
                                {replyActs.map(a => (
                                  <Tooltip key={a.id} title={`Replied ${formatTooltipDate(a.created_at)}`}>
                                    <Chip label={(a.user as any)?.display_name || a.user?.username || a.meta?.replied_by || "Unknown"} size="small" sx={{ height: 18, fontSize: 9, fontWeight: 600, bgcolor: T.greenBg, color: T.green }} />
                                  </Tooltip>
                                ))}
                              </>
                            )}
                          </Box>
                        </Box>
                      );
                    })()}

                    {selectedThread.emails?.map((email) => (
                      <Box key={email.id} sx={{ mb: 2.5 }}>
                        <Box sx={{ display: "flex", gap: 1.5 }}>
                          <Avatar sx={{ width: 36, height: 36, fontSize: 12, fontWeight: 700, flexShrink: 0, bgcolor: email.direction === "outgoing" ? T.gold : T.navy }}>
                            {initials(email.from_name, email.from_email)}
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <Box>
                                <Typography component="span" sx={{ fontSize: 13, fontWeight: 700, color: T.navy }}>{email.from_name || email.from_email}</Typography>
                                {email.direction === "outgoing" && <Chip label="Sent" size="small" sx={{ ml: 0.8, height: 16, fontSize: 9, bgcolor: T.greenBg, color: T.green, fontWeight: 600 }} />}
                                {email.direction === "outgoing" && email.read_receipts && email.read_receipts.length > 0 && (() => {
                                  const readReceipt = email.read_receipts.find(r => r.first_read_at);
                                  return readReceipt ? (
                                    <Tooltip title={`Read ${readReceipt.read_count} time${readReceipt.read_count > 1 ? "s" : ""} — First: ${fmtFullDate(readReceipt.first_read_at)} — Last: ${fmtFullDate(readReceipt.last_read_at)}`}>
                                      <Chip label={`Read ${readReceipt.read_count}x`} size="small" sx={{ ml: 0.5, height: 16, fontSize: 9, bgcolor: T.blueBg, color: T.blue, fontWeight: 600 }} />
                                    </Tooltip>
                                  ) : (
                                    <Chip label="Not read" size="small" sx={{ ml: 0.5, height: 16, fontSize: 9, bgcolor: T.bg, color: T.slateLt, fontWeight: 600 }} />
                                  );
                                })()}
                              </Box>
                              <Typography sx={{ fontSize: 10, color: T.slateLt }}>{fmtFullDate(email.received_at)}</Typography>
                            </Box>
                            <Typography sx={{ fontSize: 10, color: T.slateLt }}>To: {email.to_emails?.map(t => t.email).join(", ")}</Typography>
                            <Paper variant="outlined" sx={{ mt: 1.2, p: 2, borderRadius: 2.5, border: `1px solid ${T.line}` }}>
                              <Box sx={{ fontSize: 13, lineHeight: 1.7, color: T.navy, "& a": { color: T.blue }, "& img": { maxWidth: "100%" } }}
                                dangerouslySetInnerHTML={{ __html: email.body_html || email.body_preview || "" }} />
                            </Paper>
                            {email.attachments && email.attachments.length > 0 && (
                              <Box sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}>
                                {email.attachments.map(att => (
                                  <Chip key={att.id} icon={att.is_downloaded ? <AttachFileIcon sx={{ fontSize: 12 }} /> : <DownloadOutlinedIcon sx={{ fontSize: 12 }} />}
                                    label={`${att.file_name} (${Math.round(att.file_size / 1024)}KB)`} size="small" clickable
                                    onClick={() => att.is_downloaded && att.file_url ? window.open(`${API_BASE}/../${att.file_url}`, "_blank") : handleDownloadAttachment(att.id)}
                                    sx={{ fontSize: 10, height: 26, bgcolor: T.bg, "&:hover": { bgcolor: T.goldLight } }} />
                                ))}
                              </Box>
                            )}
                            {/* Read receipt details for outgoing emails */}
                            {email.direction === "outgoing" && email.read_receipts && email.read_receipts.length > 0 && (
                              <Box sx={{ mt: 1, p: 1, borderRadius: 1.5, bgcolor: "#f8fafc", border: `1px solid ${T.line}` }}>
                                <Typography sx={{ fontSize: 10, fontWeight: 700, color: T.slateLt, textTransform: "uppercase", mb: 0.5 }}>
                                  Read Receipts
                                </Typography>
                                {email.read_receipts.map(r => (
                                  <Box key={r.id} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.3 }}>
                                    <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: r.first_read_at ? T.green : T.slateLt, flexShrink: 0 }} />
                                    <Typography sx={{ fontSize: 11, color: T.navy, fontWeight: 500 }}>
                                      {r.recipient_email}
                                    </Typography>
                                    {r.first_read_at ? (
                                      <Typography sx={{ fontSize: 10, color: T.green, ml: "auto" }}>
                                        Read {fmtFullDate(r.first_read_at)}{r.read_count > 1 ? ` (${r.read_count}x)` : ""}
                                      </Typography>
                                    ) : (
                                      <Typography sx={{ fontSize: 10, color: T.slateLt, ml: "auto" }}>
                                        Not read yet
                                      </Typography>
                                    )}
                                  </Box>
                                ))}
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    ))}

                    {/* Internal Notes */}
                    {selectedThread.notes && selectedThread.notes.length > 0 && (
                      <Box sx={{ mt: 2, mb: 2 }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: T.slateLt, textTransform: "uppercase", mb: 1 }}>Internal Notes</Typography>
                        {selectedThread.notes.map(note => (
                          <Box key={note.id} sx={{ display: "flex", gap: 1, mb: 1, p: 1.2, borderRadius: 2, bgcolor: "#fffbeb", border: "1px solid #fef3c7" }}>
                            <NoteAddOutlinedIcon sx={{ fontSize: 14, color: "#92400e", mt: 0.2 }} />
                            <Box sx={{ flex: 1 }}>
                              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#92400e" }}>{(note.user as any)?.display_name || (note.user as any)?.username || (note.user as any)?.name || "Staff"}</Typography>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                  <Typography sx={{ fontSize: 10, color: T.slateLt }}>{formatShortDate(note.created_at)}</Typography>
                                  <IconButton size="small" onClick={() => handleDeleteNote(note.id)} sx={{ p: 0 }}><DeleteOutlineIcon sx={{ fontSize: 12, color: T.slateLt }} /></IconButton>
                                </Box>
                              </Box>
                              <Typography sx={{ fontSize: 12, color: "#78350f", mt: 0.3 }}>{note.note}</Typography>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    )}

                    {/* Add note */}
                    <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                      <TextField size="small" fullWidth placeholder="Add note..." value={noteText} onChange={e => setNoteText(e.target.value)}
                        onKeyDown={e => e.key === "Enter" && !e.shiftKey && handleAddNote()}
                        sx={{ "& .MuiInputBase-root": { fontSize: 12, borderRadius: 2, bgcolor: T.bg, "& fieldset": { border: "none" } } }} />
                      <Button size="small" onClick={handleAddNote} disabled={noteAdding || !noteText.trim()}
                        sx={{ textTransform: "none", fontSize: 11, bgcolor: T.goldLight, color: "#8b6914", borderRadius: 2, minWidth: 60, "&:hover": { bgcolor: T.gold, color: T.white } }}>
                        Note
                      </Button>
                    </Box>
                  </>
                )}

                {detailTab === "timeline" && (
                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: T.navy, mb: 2 }}>Activity Timeline</Typography>

                    {/* ── Viewed By Summary ── */}
                    {(() => {
                      const viewActivities = activities.filter(a => a.type === "thread_viewed");
                      if (viewActivities.length === 0) return null;
                      const uniqueViewers = new Map<string, string>();
                      viewActivities.forEach(a => {
                        const name = (a.user as any)?.display_name || a.user?.username || a.meta?.viewed_by || "Unknown";
                        if (!uniqueViewers.has(name)) uniqueViewers.set(name, a.created_at);
                      });
                      return (
                        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 2, bgcolor: "#f0f4ff", border: `1px solid ${T.blue}30` }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                            <VisibilityOutlinedIcon sx={{ fontSize: 14, color: T.blue }} />
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: T.navy }}>
                              Viewed by {uniqueViewers.size} staff member{uniqueViewers.size > 1 ? "s" : ""}
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                            {Array.from(uniqueViewers.entries()).map(([name, time]) => (
                              <Tooltip key={name} title={`First viewed ${formatTooltipDate(time)}`}>
                                <Chip label={name} size="small" sx={{ height: 20, fontSize: 10, fontWeight: 600, bgcolor: T.blue + "15", color: T.blue, border: `1px solid ${T.blue}30` }} />
                              </Tooltip>
                            ))}
                          </Box>
                        </Paper>
                      );
                    })()}

                    {activities.length === 0 ? (
                      <Typography sx={{ fontSize: 12, color: T.slateLt, textAlign: "center", py: 4 }}>No activities yet</Typography>
                    ) : (
                      <Box sx={{ position: "relative", pl: 3 }}>
                        <Box sx={{ position: "absolute", left: 8, top: 0, bottom: 0, width: 2, bgcolor: T.line }} />
                        {activities.map(act => {
                          const typeColors: Record<string, string> = {
                            email_received: T.blue, email_sent: T.green, note_added: "#92400e",
                            status_changed: T.purple, priority_changed: "#f59e0b", assignment_changed: "#6366f1",
                            task_created: "#14b8a6", quote_created: T.green, booking_created: T.orange,
                            lead_created: T.blue, team_state_changed: T.purple, automation: "#8b5cf6",
                            customer_linked: "#059669", thread_viewed: "#64748b",
                          };
                          const typeIcons: Record<string, React.ReactNode> = {
                            thread_viewed: <VisibilityOutlinedIcon sx={{ fontSize: 10, color: "#64748b" }} />,
                            email_sent: <ReplyOutlinedIcon sx={{ fontSize: 10, color: T.green }} />,
                            email_received: <MailOutlineIcon sx={{ fontSize: 10, color: T.blue }} />,
                          };
                          const color = typeColors[act.type] || T.slate;
                          return (
                            <Box key={act.id} sx={{ display: "flex", gap: 1.5, mb: 2, position: "relative" }}>
                              <Box sx={{ width: 18, height: 18, borderRadius: "50%", flexShrink: 0, bgcolor: color + "20", border: `2px solid ${color}`, position: "absolute", left: -11.5, zIndex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                {typeIcons[act.type] || null}
                              </Box>
                              <Box sx={{ ml: 1.5 }}>
                                <Typography sx={{ fontSize: 11, color: T.navy, fontWeight: 500 }}>{act.description || act.type}</Typography>
                                <Typography sx={{ fontSize: 10, color: T.slateLt }}>
                                  {(act.user as any)?.display_name || act.user?.username || "System"} &middot; {formatDateTime(act.created_at)}
                                </Typography>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                )}

                {detailTab === "tasks" && (
                  <Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: T.navy }}>Tasks</Typography>
                      <Button size="small" startIcon={<TaskAltOutlinedIcon sx={{ fontSize: 14 }} />}
                        onClick={async () => {
                          const task = await createTask({ thread_id: selectedThread.id, title: `Follow up: ${selectedThread.subject}`, priority: selectedThread.priority || "medium", assigned_to: selectedThread.assigned_to });
                          setThreadTasks(prev => [task, ...prev]);
                        }}
                        sx={{ textTransform: "none", fontSize: 11, color: "#14b8a6" }}>
                        Add Task
                      </Button>
                    </Box>
                    {threadTasks.length === 0 ? (
                      <Typography sx={{ fontSize: 12, color: T.slateLt, textAlign: "center", py: 4 }}>No tasks for this thread</Typography>
                    ) : threadTasks.map(task => (
                      <Paper key={task.id} variant="outlined" sx={{ p: 1.5, mb: 1, borderRadius: 2, border: `1px solid ${T.line}` }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                          <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                            <IconButton size="small" onClick={async () => {
                              const updated = await updateTask(task.id, { status: task.status === "completed" ? "pending" : "completed" });
                              setThreadTasks(prev => prev.map(t => t.id === task.id ? updated : t));
                            }}>
                              <TaskAltOutlinedIcon sx={{ fontSize: 18, color: task.status === "completed" ? T.green : T.slateLt }} />
                            </IconButton>
                            <Box>
                              <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.navy, textDecoration: task.status === "completed" ? "line-through" : "none" }}>{task.title}</Typography>
                              <Box sx={{ display: "flex", gap: 1, mt: 0.3 }}>
                                <Chip label={task.priority} size="small" sx={{ height: 16, fontSize: 8, bgcolor: PRIORITY_COLORS[task.priority] + "20", color: PRIORITY_COLORS[task.priority] }} />
                                {task.due_at && <Typography sx={{ fontSize: 10, color: new Date(task.due_at) < new Date() && task.status !== "completed" ? T.red : T.slateLt }}>Due {formatShortDate(task.due_at)}</Typography>}
                              </Box>
                            </Box>
                          </Box>
                          <IconButton size="small" onClick={async () => { await deleteTask(task.id); setThreadTasks(prev => prev.filter(t => t.id !== task.id)); }}>
                            <DeleteOutlineIcon sx={{ fontSize: 14, color: T.slateLt }} />
                          </IconButton>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                )}
              </Box>

              {/* ── Right sidebar: Customer Insights + Booking History + AI Panel ── */}
              {(showAiPanel || selectedThread.booking || (insights && (insights.total_inquiries > 0 || insights.total_bookings > 0))) && (
                <Box sx={{ width: 260, borderLeft: `1px solid ${T.line}`, overflow: "auto", flexShrink: 0, bgcolor: T.cream }}>
                  {/* ── Booking History Card ── */}
                  {selectedThread.booking && (() => {
                    const bk = selectedThread.booking;
                    return (
                      <Box sx={{ p: 2, borderBottom: `1px solid ${T.line}` }}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <BookOnlineOutlinedIcon sx={{ fontSize: 14, color: T.orange }} />
                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.navy }}>Booking History</Typography>
                          </Box>
                          <Chip label={bk.status || "—"} size="small" sx={{ height: 18, fontSize: 9, fontWeight: 700, textTransform: "capitalize",
                            bgcolor: bk.status === "complete" ? T.greenBg : bk.status === "cancel" ? T.redBg : T.orangeBg,
                            color: bk.status === "complete" ? T.green : bk.status === "cancel" ? T.red : T.orange }} />
                        </Box>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8 }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                            <Typography sx={{ fontSize: 11, color: T.slate }}>Ref #</Typography>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: T.navy, cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                              onClick={() => navigate(`/banquet/booking/${bk.id}`)}>{bk.bookingRef}</Typography>
                          </Box>
                          {bk.agentName && (
                            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                              <Typography sx={{ fontSize: 11, color: T.slate }}>Admin</Typography>
                              <Typography sx={{ fontSize: 11, fontWeight: 600, color: T.navy }}>{bk.agentName}</Typography>
                            </Box>
                          )}
                          {bk.creator && (
                            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                              <Typography sx={{ fontSize: 11, color: T.slate }}>Created By</Typography>
                              <Typography sx={{ fontSize: 11, fontWeight: 600, color: T.navy }}>{(bk.creator as any).display_name || bk.creator.username}</Typography>
                            </Box>
                          )}
                          {bk.eventType && (
                            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                              <Typography sx={{ fontSize: 11, color: T.slate }}>Event</Typography>
                              <Typography sx={{ fontSize: 11, fontWeight: 600, color: T.navy, textTransform: "capitalize" }}>{bk.eventType.replace(/_/g, " ")}</Typography>
                            </Box>
                          )}
                          {bk.eventDate && (
                            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                              <Typography sx={{ fontSize: 11, color: T.slate }}>Date</Typography>
                              <Typography sx={{ fontSize: 11, fontWeight: 600, color: T.navy }}>{formatShortDate(bk.eventDate)}</Typography>
                            </Box>
                          )}
                          {(bk.startTime || bk.timeSlotLabel) && (
                            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                              <Typography sx={{ fontSize: 11, color: T.slate }}>Time</Typography>
                              <Typography sx={{ fontSize: 11, fontWeight: 600, color: T.navy }}>
                                {bk.startTime && bk.endTime ? `${bk.startTime} – ${bk.endTime}` : bk.timeSlotLabel || bk.timeSlot || "—"}
                              </Typography>
                            </Box>
                          )}
                          {bk.expectedGuests && (
                            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                              <Typography sx={{ fontSize: 11, color: T.slate }}>Guests</Typography>
                              <Typography sx={{ fontSize: 11, fontWeight: 600, color: T.navy }}>{bk.expectedGuests}</Typography>
                            </Box>
                          )}
                          {bk.halls && bk.halls.length > 0 && (
                            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                              <Typography sx={{ fontSize: 11, color: T.slate }}>Hall(s)</Typography>
                              <Typography sx={{ fontSize: 11, fontWeight: 600, color: T.navy }}>{bk.halls.map(h => h.hallName).join(", ")}</Typography>
                            </Box>
                          )}
                          {bk.grandTotal != null && (
                            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                              <Typography sx={{ fontSize: 11, color: T.slate }}>Total</Typography>
                              <Typography sx={{ fontSize: 11, fontWeight: 700, color: T.green }}>${Number(bk.grandTotal).toLocaleString()}</Typography>
                            </Box>
                          )}
                          {bk.created_at && (
                            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                              <Typography sx={{ fontSize: 11, color: T.slate }}>Created</Typography>
                              <Typography sx={{ fontSize: 11, color: T.slateLt }}>{formatShortDate(bk.created_at)}</Typography>
                            </Box>
                          )}
                        </Box>
                        <Button size="small" fullWidth variant="outlined" startIcon={<OpenInNewOutlinedIcon sx={{ fontSize: 14 }} />}
                          onClick={() => navigate(`/banquet/booking/${bk.id}`)}
                          sx={{ mt: 1.5, textTransform: "none", fontSize: 10, fontWeight: 700, color: T.orange, borderColor: T.orange + "40", borderRadius: 1.5, "&:hover": { bgcolor: T.orangeBg, borderColor: T.orange } }}>
                          Open Booking
                        </Button>
                      </Box>
                    );
                  })()}

                  {insights && (insights.total_inquiries > 0 || insights.total_bookings > 0) && (
                    <Box sx={{ p: 2, borderBottom: `1px solid ${T.line}` }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1.5 }}>
                        <GroupsOutlinedIcon sx={{ fontSize: 14, color: T.navy }} />
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.navy }}>Customer Insights</Typography>
                      </Box>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8 }}>
                        {[
                          { l: "Past Inquiries", v: insights.total_inquiries },
                          { l: "Total Bookings", v: insights.total_bookings },
                          { l: "Lifetime Value", v: `$${insights.lifetime_value?.toLocaleString()}`, c: T.green },
                        ].map(r => (
                          <Box key={r.l} sx={{ display: "flex", justifyContent: "space-between" }}>
                            <Typography sx={{ fontSize: 11, color: T.slate }}>{r.l}</Typography>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: r.c || T.navy }}>{r.v}</Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}
                  {showAiPanel && (
                    <Box sx={{ p: 2 }}>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <AutoAwesomeOutlinedIcon sx={{ fontSize: 14, color: T.purple }} />
                          <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.navy }}>AI Assist</Typography>
                        </Box>
                        <IconButton size="small" onClick={() => setShowAiPanel(false)}><ChevronRightIcon sx={{ fontSize: 16 }} /></IconButton>
                      </Box>
                      {aiLoading ? (
                        <Box sx={{ textAlign: "center", py: 3 }}><CircularProgress size={24} sx={{ color: T.purple }} /></Box>
                      ) : aiError ? (
                        <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: "#fff1f1", border: "1px solid #fca5a5" }}>
                          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#b91c1c", mb: 0.5 }}>AI Analysis Failed</Typography>
                          <Typography sx={{ fontSize: 11, color: "#7f1d1d", lineHeight: 1.5 }}>{aiError}</Typography>
                        </Box>
                      ) : aiData ? (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                          <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: T.white, border: `1px solid ${T.line}` }}>
                            <Typography sx={{ fontSize: 10, fontWeight: 700, color: T.slateLt, textTransform: "uppercase", mb: 0.5 }}>Intent</Typography>
                            <Chip label={aiData.intent} size="small" sx={{ height: 22, fontSize: 11, fontWeight: 600, bgcolor: INTENT_COLORS[aiData.intent]?.bg || T.bg, color: INTENT_COLORS[aiData.intent]?.tx || T.slate }} />
                          </Box>
                          {(aiData.event_type || aiData.guest_count) && (
                            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: T.white, border: `1px solid ${T.line}` }}>
                              <Typography sx={{ fontSize: 10, fontWeight: 700, color: T.slateLt, textTransform: "uppercase", mb: 0.5 }}>Extracted Data</Typography>
                              {aiData.event_type && <Typography sx={{ fontSize: 11, color: T.navy }}>Event: <strong>{aiData.event_type}</strong></Typography>}
                              {aiData.guest_count && <Typography sx={{ fontSize: 11, color: T.navy }}>Guests: <strong>{aiData.guest_count}</strong></Typography>}
                              {aiData.event_date && <Typography sx={{ fontSize: 11, color: T.navy }}>Date: <strong>{aiData.event_date}</strong></Typography>}
                              {aiData.budget && <Typography sx={{ fontSize: 11, color: T.navy }}>Budget: <strong>{aiData.budget}</strong></Typography>}
                            </Box>
                          )}
                          {aiData.suggested_reply && (
                            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: T.purpleBg, border: "1px solid #e9e5ff" }}>
                              <Typography sx={{ fontSize: 10, fontWeight: 700, color: T.purple, textTransform: "uppercase", mb: 0.5 }}>Suggested Reply</Typography>
                              <Typography sx={{ fontSize: 11, color: T.navy, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{aiData.suggested_reply}</Typography>
                              <Button size="small" variant="contained" onClick={() => { setReplyTo(selectedThread.emails?.slice(-1)[0] || null); setComposeOpen(true); }}
                                sx={{ mt: 1, textTransform: "none", fontSize: 10, bgcolor: T.purple, borderRadius: 1.5, "&:hover": { bgcolor: T.navy } }}>
                                Use Reply
                              </Button>
                            </Box>
                          )}
                        </Box>
                      ) : (
                        <Typography sx={{ fontSize: 12, color: T.slateLt, textAlign: "center", py: 3 }}>Click "AI Analyze" to get insights</Typography>
                      )}
                    </Box>
                  )}

                  {/* ── Customer History (info-only, non-blocking) ── */}
                  {customerHistory.length > 0 && (
                    <Box sx={{ p: 2, borderTop: `1px solid ${T.line}` }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
                        <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#92400e", textTransform: "uppercase", letterSpacing: 0.5 }}>
                          ⚠️ Customer History
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: 11, color: T.slate, mb: 1.5, lineHeight: 1.5 }}>
                        This customer has previous leads. A new lead was created for this inquiry. You may optionally link to an existing one instead.
                      </Typography>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.6 }}>
                        {customerHistory.map(lead => (
                          <Box key={lead.id} sx={{ p: 1, borderRadius: 1.5, bgcolor: "#fef3c7", border: "1px solid #fde68a", display: "flex", alignItems: "center", gap: 1 }}>
                            <Box flex={1} minWidth={0}>
                              <Typography sx={{ fontSize: 11, fontWeight: 700, color: T.navy }} noWrap>{lead.name}</Typography>
                              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 0.2 }}>
                                {lead.event_type && (
                                  <Typography sx={{ fontSize: 10, color: T.slate, textTransform: "capitalize" }}>{lead.event_type.replace(/_/g, " ")}</Typography>
                                )}
                                <Chip label={STATUS_CONFIG[lead.status as LeadStatus]?.label || lead.status} size="small"
                                  sx={{ height: 15, fontSize: 9, fontWeight: 700, bgcolor: STATUS_CONFIG[lead.status as LeadStatus]?.bgColor, color: STATUS_CONFIG[lead.status as LeadStatus]?.color }} />
                                {lead.created_at && (
                                  <Typography sx={{ fontSize: 10, color: T.slateLt }}>{formatShortDate(lead.created_at)}</Typography>
                                )}
                              </Box>
                            </Box>
                            <Button size="small" variant="outlined" disabled={linkingExisting}
                              onClick={() => setLinkConfirmLead(lead)}
                              sx={{ textTransform: "none", fontSize: 9, fontWeight: 700, color: "#92400e", borderColor: "#fde68a", borderRadius: 1.5, flexShrink: 0, "&:hover": { bgcolor: "#fef3c7", borderColor: "#d97706" } }}>
                              Link
                            </Button>
                          </Box>
                        ))}
                      </Box>
                      <Button size="small" fullWidth variant="outlined"
                        onClick={() => window.open(`/leads/inquiries`, "_blank")}
                        sx={{ mt: 1.5, textTransform: "none", fontSize: 10, color: T.slate, borderColor: T.line, borderRadius: 1.5 }}>
                        View All Previous Leads
                      </Button>
                    </Box>
                  )}

                  {/* ── Link Existing Confirmation Dialog ── */}
                  {linkConfirmLead && (
                    <Box sx={{ position: "absolute", inset: 0, bgcolor: "rgba(0,0,0,0.35)", zIndex: 50, display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}>
                      <Box sx={{ bgcolor: "#fff", borderRadius: 3, p: 3, boxShadow: "0 20px 60px rgba(0,0,0,0.2)", maxWidth: 320, width: "100%" }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.navy, mb: 1 }}>Link to Existing Lead?</Typography>
                        <Typography sx={{ fontSize: 12, color: T.slate, mb: 2, lineHeight: 1.6 }}>
                          This will reassign this email thread to the existing lead <strong>{linkConfirmLead.name}</strong>. The new lead created for this inquiry will be unlinked.
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                          <Button size="small" onClick={() => setLinkConfirmLead(null)} sx={{ color: T.slate, textTransform: "none" }}>Cancel</Button>
                          <Button size="small" variant="contained" disabled={linkingExisting}
                            onClick={async () => {
                              if (!linkConfirmLead || !selectedThread) return;
                              setLinkingExisting(true);
                              try {
                                await linkEmailToLead(selectedThread.id, linkConfirmLead.id);
                                const fullLead = await fetchLeadThread(linkConfirmLead.id);
                                setLinkedLead(fullLead);
                                setSelectedThread(prev => prev ? { ...prev, linked_thread_id: linkConfirmLead.id } : null);
                                setThreads(prev => prev.map(t => t.id === selectedThread.id ? { ...t, linked_thread_id: linkConfirmLead.id } : t));
                                setCustomerHistory([]);
                                setAutoConverted(false);
                                setLinkConfirmLead(null);
                              } catch {}
                              setLinkingExisting(false);
                            }}
                            sx={{ textTransform: "none", fontSize: 12, fontWeight: 700, bgcolor: "#d97706", "&:hover": { bgcolor: "#b45309" }, borderRadius: 1.5 }}>
                            {linkingExisting ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : "Confirm Link"}
                          </Button>
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </Box>

            {/* ── Quick Reply Bar ── */}
            <Box sx={{ px: 3, py: 1.5, borderTop: `1px solid ${T.line}`, bgcolor: T.cream }}>
              <Button fullWidth variant="outlined" onClick={() => { setReplyTo(selectedThread.emails?.slice(-1)[0] || null); setComposeOpen(true); }}
                sx={{ textTransform: "none", fontSize: 12, color: T.slate, borderColor: T.line, borderRadius: 2.5, py: 1, justifyContent: "flex-start", "&:hover": { borderColor: T.gold, bgcolor: T.white } }}>
                Click to reply...
              </Button>
            </Box>
          </>
        )}
      </Box>

      {/* ═══════════ MODALS ═══════════ */}
      <ComposeModal open={composeOpen} onClose={() => { setComposeOpen(false); setReplyTo(null); }} accounts={accounts} replyTo={replyTo}
        threadId={selectedThread?.id} initialBody={aiData?.suggested_reply}
        onSent={() => { setComposeOpen(false); setReplyTo(null); loadThreads(); loadMeta(); if (selectedThread) handleSelectThread(selectedThread); }} />

      {selectedThread && (
        <>
          <CreateLeadModal open={leadModalOpen} onClose={() => setLeadModalOpen(false)} threadId={selectedThread.id}
            onCreated={(lead) => {
              setSelectedThread(prev => prev ? { ...prev, lead_id: lead.id, lead } : null);
              setThreads(prev => prev.map(t => t.id === selectedThread.id ? { ...t, lead_id: lead.id } : t));
            }} />
          <CreateQuoteModal open={quoteModalOpen} onClose={() => setQuoteModalOpen(false)} threadId={selectedThread.id}
            onCreated={(quote) => {
              setSelectedThread(prev => prev ? { ...prev, quote_id: quote.id, quote } : null);
              setThreads(prev => prev.map(t => t.id === selectedThread.id ? { ...t, quote_id: quote.id } : t));
            }} />
          <ConvertBookingModal open={bookingModalOpen} onClose={() => setBookingModalOpen(false)} threadId={selectedThread.id}
            onCreated={(booking) => {
              setSelectedThread(prev => prev ? { ...prev, booking_id: booking.id, booking } : null);
              setThreads(prev => prev.map(t => t.id === selectedThread.id ? { ...t, booking_id: booking.id } : t));
            }} />
          <AssignThreadModal open={assignModalOpen} onClose={() => setAssignModalOpen(false)} threadId={selectedThread.id}
            currentAssignee={selectedThread.assigned_to} currentPriority={selectedThread.priority}
            currentDueAt={selectedThread.due_at}
            onUpdated={(updated) => {
              setSelectedThread(prev => prev ? { ...prev, ...updated } : null);
              setThreads(prev => prev.map(t => t.id === selectedThread.id ? { ...t, ...updated } : t));
            }} />
          <ConvertToLeadModal
            open={convertLeadOpen}
            onClose={() => setConvertLeadOpen(false)}
            emailThreadId={selectedThread.id}
            prefillName={selectedThread.emails?.[0]?.from_name || ""}
            prefillEmail={selectedThread.customer_email || selectedThread.emails?.[0]?.from_email || ""}
            aiData={selectedThread.ai_extracted_data || null}
            onConverted={() => {
              setConvertLeadOpen(false);
              setConversionStatus("idle");
              setCustomerHistory([]);
              setAutoConverted(true);
              loadThreads();
              handleSelectThread(selectedThread);
            }}
          />
        </>
      )}

      {/* Lead Thread Drawer */}
      <LeadThreadDrawer
        open={leadDrawerOpen}
        threadId={leadDrawerThreadId}
        onClose={() => setLeadDrawerOpen(false)}
        onUpdated={() => {
          loadThreads();
          if (selectedThread) handleSelectThread(selectedThread);
        }}
      />
    </Box>
  );
};

export default EmailInbox;

/* ────────────────────────────────────────────────────────────────────
   LeadMatchBanner — banner above the email body that:
     1. carries the strict AI classifier's state badge
     2. gates the conversion action set per state
     3. always offers a [Mark Private] escape hatch

   States vs actions:
     full_match + linkedThread (auto-created)
                     → green "Lead Created Automatically" with
                        [Open Lead] [View Timeline] · NO Create/Link/Ignore
     full_match without linkedThread (auto-create failed fallback)
                     → [Create Lead]  [Link Existing]  [Ignore]
     existing_match  → [Link to Lead] [Create Anyway]  [Ignore]
     partial_inquiry → [Reclassify]   [Convert Anyway] [Ignore]
     needs_review    → [Reclassify]   [Convert]        [Ignore]
     non_lead        → muted strip — just [Reclassify] + Private toggle
     null (unclassified) → [Reclassify] only

   Per the AI Lead Detection spec: a full_match auto-creates the lead
   on sync; the inbox only shows "open the lead" affordances after the
   fact. Duplicate / merge decisions live in Lead View, not here. */
function LeadMatchBanner({
  state, confidence, reasons, ignored, linkedThread,
  onCreateLead, onLinkExisting, onIgnore, onReclassify, onMarkPrivate,
  onOpenLead, onViewTimeline,
}: {
  state: LeadMatchState | null;
  confidence: number | null;
  reasons: string[];
  ignored: boolean;
  /** Eager-loaded linked LeadThread summary. Present when the lead
   *  has been created (auto on full_match, or manually). Drives the
   *  green auto-created banner — when present + state=full_match the
   *  banner shows "Lead Created Automatically — Lead #..." with
   *  navigation buttons instead of the 3-button conversion bar. */
  linkedThread?: { id: number; lead_number: string | null; name: string } | null;
  onCreateLead: () => void;
  onLinkExisting: () => void;
  onIgnore: () => void;
  onReclassify: () => void;
  onMarkPrivate: () => void;
  onOpenLead: (leadId: number) => void;
  onViewTimeline: (leadId: number) => void;
}) {
  // ── Auto-created short-circuit ─────────────────────────────────────
  // If a lead has already been auto-created from this full_match
  // thread, render the success banner and bail before the rest of the
  // switch. The 3-button conversion bar is intentionally suppressed —
  // there is no manual action to take here anymore.
  if (state === "full_match" && linkedThread) {
    const leadLabel = linkedThread.lead_number
      ? `Lead #${linkedThread.lead_number}`
      : `Lead #${linkedThread.id}`;
    return (
      <Box sx={{
        bgcolor: "#f0fdf4",
        borderBottom: "2px solid #bbf7d0",
        px: 3, py: 1.5,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 1.5, flexWrap: "wrap",
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0, flex: 1 }}>
          <Box sx={{
            display: "inline-flex", alignItems: "center", gap: 0.5,
            bgcolor: "#dcfce7", color: "#16a34a",
            border: "1px solid #16a34a33",
            borderRadius: "999px",
            px: 0.9, py: 0.3,
            fontSize: 10, fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            flexShrink: 0,
          }}>
            <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: "#16a34a" }} />
            Lead Created
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#15803d", lineHeight: 1.2 }} noWrap>
              ✅ Lead Created Automatically
            </Typography>
            <Typography sx={{ fontSize: 11, color: "#22863a", mt: 0.1 }} noWrap>
              AI detected a confirmed booking inquiry and created {leadLabel}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", flexWrap: "wrap" }}>
          <Button size="small" variant="contained"
            onClick={() => onOpenLead(linkedThread.id)}
            sx={{ textTransform: "none", fontSize: 11, fontWeight: 700, bgcolor: "#16a34a", "&:hover": { bgcolor: "#15803d" } }}>
            Open Lead
          </Button>
          <Button size="small" variant="outlined"
            onClick={() => onViewTimeline(linkedThread.id)}
            sx={{ textTransform: "none", fontSize: 11, color: "#16a34a", borderColor: "#bbf7d0", "&:hover": { bgcolor: "#dcfce7" } }}>
            View Timeline
          </Button>
          <Tooltip title="Mark Private">
            <IconButton size="small" onClick={onMarkPrivate} sx={{ bgcolor: "transparent", "&:hover": { bgcolor: "rgba(0,0,0,0.04)" } }}>
              <LockOutlinedIcon sx={{ fontSize: 16, color: "#64748b" }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    );
  }
  // Palette + copy per state — drives the banner background, badge
  // text, and primary microcopy. Reasons stack into a small tooltip on
  // the badge so the rep can audit the classifier's call.
  const cfg = (() => {
    switch (state) {
      case "full_match":
        return {
          bg: "#f0fdf4", border: "#bbf7d0",
          dot: "#16a34a", chip: "#16a34a", chipBg: "#dcfce7",
          label: "Full Lead Match",
          title: "Full lead match",
          subtitle: "AI is confident this is a booking inquiry",
        };
      case "existing_match":
        return {
          bg: "#eff6ff", border: "#bfdbfe",
          dot: "#2563eb", chip: "#2563eb", chipBg: "#dbeafe",
          label: "Existing Lead Match",
          title: "Possible existing lead match",
          subtitle: "Sender matches an existing CRM record — link before creating",
        };
      case "partial_inquiry":
        return {
          bg: "#fefce8", border: "#fde68a",
          dot: "#ca8a04", chip: "#a16207", chipBg: "#fef9c3",
          label: "Partial Match",
          title: "🟡 Partial Match Detected",
          subtitle: "AI found a possible inquiry but requires review before lead creation",
        };
      case "needs_review":
        return {
          bg: "#fff7ed", border: "#fed7aa",
          dot: "#ea580c", chip: "#c2410c", chipBg: "#ffedd5",
          label: "Needs Review",
          title: "Needs human review",
          subtitle: "Ambiguous — please verify before converting",
        };
      case "non_lead":
        return {
          bg: "#f8fafc", border: "#e5e7eb",
          dot: "#94a3b8", chip: "#64748b", chipBg: "#f1f5f9",
          label: ignored ? "Ignored" : "Not a Lead",
          title: ignored ? "Marked as not a lead" : "AI: not a booking inquiry",
          subtitle: "Kept in the inbox — no CRM record will be created",
        };
      default:
        return {
          bg: "#f8fafc", border: "#e5e7eb",
          dot: "#94a3b8", chip: "#64748b", chipBg: "#f1f5f9",
          label: "Unclassified",
          title: "Not yet classified",
          subtitle: "Click Reclassify to run the AI lead-match check",
        };
    }
  })();

  const tooltipBody = (
    <Box sx={{ p: 0.5 }}>
      <Typography sx={{ fontSize: 11, fontWeight: 700, mb: 0.4 }}>
        AI lead match · {state ?? "unclassified"}
      </Typography>
      {confidence !== null && (
        <Typography sx={{ fontSize: 10.5, color: "#cbd5e1", mb: 0.6 }}>
          confidence {(confidence * 100).toFixed(0)}%
        </Typography>
      )}
      {reasons && reasons.length > 0 && (
        <Box component="ul" sx={{ m: 0, pl: 2 }}>
          {reasons.map((r, i) => (
            <Box component="li" key={i} sx={{ fontSize: 10.5, lineHeight: 1.5 }}>{r}</Box>
          ))}
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{
      bgcolor: cfg.bg,
      borderBottom: `2px solid ${cfg.border}`,
      px: 3, py: 1.5,
      display: "flex", alignItems: "center", justifyContent: "space-between",
      gap: 1.5, flexWrap: "wrap",
    }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0, flex: 1 }}>
        <Tooltip title={tooltipBody} arrow placement="bottom-start">
          <Box sx={{
            display: "inline-flex", alignItems: "center", gap: 0.5,
            bgcolor: cfg.chipBg, color: cfg.chip,
            border: `1px solid ${cfg.chip}33`,
            borderRadius: "999px",
            px: 0.9, py: 0.3,
            fontSize: 10, fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            cursor: "help",
            flexShrink: 0,
          }}>
            <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: cfg.dot }} />
            {cfg.label}
          </Box>
        </Tooltip>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a", lineHeight: 1.2 }} noWrap>
            {cfg.title}
          </Typography>
          <Typography sx={{ fontSize: 11, color: "#64748b", mt: 0.1 }} noWrap>
            {cfg.subtitle}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", flexWrap: "wrap" }}>
        {state === "full_match" && (
          <>
            <Button size="small" variant="contained" startIcon={<PersonAddOutlinedIcon sx={{ fontSize: 14 }} />}
              onClick={onCreateLead}
              sx={{ textTransform: "none", fontSize: 11, fontWeight: 700, bgcolor: "#16a34a", "&:hover": { bgcolor: "#15803d" } }}>
              Create Lead
            </Button>
            <Button size="small" variant="outlined" startIcon={<LinkOutlinedIcon sx={{ fontSize: 14 }} />}
              onClick={onLinkExisting}
              sx={{ textTransform: "none", fontSize: 11, color: "#16a34a", borderColor: "#bbf7d0", "&:hover": { bgcolor: "#dcfce7" } }}>
              Link Existing
            </Button>
            <Button size="small" onClick={onIgnore}
              sx={{ textTransform: "none", fontSize: 11, color: "#64748b", "&:hover": { bgcolor: "rgba(0,0,0,0.04)" } }}>
              Ignore
            </Button>
          </>
        )}
        {state === "existing_match" && (
          <>
            <Button size="small" variant="contained" startIcon={<LinkOutlinedIcon sx={{ fontSize: 14 }} />}
              onClick={onLinkExisting}
              sx={{ textTransform: "none", fontSize: 11, fontWeight: 700, bgcolor: "#2563eb", "&:hover": { bgcolor: "#1d4ed8" } }}>
              Review Match
            </Button>
            <Button size="small" variant="outlined"
              onClick={onCreateLead}
              sx={{ textTransform: "none", fontSize: 11, color: "#2563eb", borderColor: "#bfdbfe", "&:hover": { bgcolor: "#dbeafe" } }}>
              Create Anyway
            </Button>
            <Button size="small" onClick={onIgnore}
              sx={{ textTransform: "none", fontSize: 11, color: "#64748b", "&:hover": { bgcolor: "rgba(0,0,0,0.04)" } }}>
              Ignore
            </Button>
          </>
        )}
        {state === "partial_inquiry" && (
          <>
            <Button size="small" variant="outlined" onClick={onReclassify}
              sx={{ textTransform: "none", fontSize: 11, color: "#a16207", borderColor: "#fde68a", "&:hover": { bgcolor: "#fef9c3" } }}>
              Reclassify
            </Button>
            <Button size="small" variant="contained" onClick={onCreateLead}
              sx={{ textTransform: "none", fontSize: 11, fontWeight: 700, bgcolor: "#ca8a04", "&:hover": { bgcolor: "#a16207" } }}>
              Convert Anyway
            </Button>
            <Button size="small" onClick={onIgnore}
              sx={{ textTransform: "none", fontSize: 11, color: "#64748b", "&:hover": { bgcolor: "rgba(0,0,0,0.04)" } }}>
              Ignore
            </Button>
          </>
        )}
        {state === "needs_review" && (
          <>
            <Button size="small" variant="outlined" onClick={onReclassify}
              sx={{ textTransform: "none", fontSize: 11, color: "#c2410c", borderColor: "#fed7aa", "&:hover": { bgcolor: "#ffedd5" } }}>
              Reclassify
            </Button>
            <Button size="small" variant="contained" onClick={onCreateLead}
              sx={{ textTransform: "none", fontSize: 11, fontWeight: 700, bgcolor: "#ea580c", "&:hover": { bgcolor: "#c2410c" } }}>
              Convert
            </Button>
            <Button size="small" onClick={onIgnore}
              sx={{ textTransform: "none", fontSize: 11, color: "#64748b", "&:hover": { bgcolor: "rgba(0,0,0,0.04)" } }}>
              Ignore
            </Button>
          </>
        )}
        {(state === "non_lead" || state === null) && (
          <Button size="small" onClick={onReclassify}
            sx={{ textTransform: "none", fontSize: 11, color: "#64748b", "&:hover": { bgcolor: "rgba(0,0,0,0.04)" } }}>
            Reclassify
          </Button>
        )}
        <Tooltip title="Mark Private">
          <IconButton size="small" onClick={onMarkPrivate} sx={{ bgcolor: "transparent", "&:hover": { bgcolor: "rgba(0,0,0,0.04)" } }}>
            <LockOutlinedIcon sx={{ fontSize: 16, color: "#64748b" }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}
